import { useEffect, useState, useRef, useMemo, useCallback, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { UI, EASE_OUT } from "@/lib/pohyb";
import { toast } from "sonner";
import {
  X, Phone, MessageCircle, Mail, MapPin, Trash2, FileCheck, Sparkles,
  Users, Calendar, TrendingUp, Clock, Briefcase, Heart,
  ClipboardList, History, AlertTriangle, IdCard, Wallet, Plus, Pencil,
  FileText, Star, Globe, Building2, Map as MapIcon, UserSquare2, Camera, UserPlus, Landmark, Home, Radar,
} from "lucide-react";
import { PIPELINE_STAGES, CONTACT_SOURCES } from "@/data/crmData";
import { popisZdroje } from "@/lib/crm/puvodLeadu";
import { bjNaPenize, hlaskaBezKoeficientu } from "@/lib/bjPenize";
import { odebirejZemi, zemeTenanta } from "@/lib/tenantMena";
import OdkazNaDoporuceni from "@/components/doporuceni/OdkazNaDoporuceni";
import useOdmenaTipare, { usePodpisDoporuceni } from "@/components/doporuceni/useOdmenaTipare";
import api from "@/lib/api";
import PortfolioDonutCard from "@/components/crm/PortfolioDonutCard";
import ContractsCleanList from "@/components/crm/ContractsCleanList";
import FamilyPicker from "@/components/crm/FamilyPicker";
import HouseholdGraph from "@/components/crm/HouseholdGraph";
import LeakageRadar from "@/components/crm/LeakageRadar";
import HistoryTimeline from "@/components/crm/HistoryTimeline";
import ClientDiscussionTab from "@/components/crm/ClientDiscussionTab";
import ContractEditor from "@/components/crm/ContractEditor";
import TagsEditor from "@/components/crm/TagsEditor";
import DalsiUdajeTab from "@/components/crm/DalsiUdajeTab";
import AddressAutocomplete from "@/components/crm/AddressAutocomplete";
import { adresyZeme } from "@/lib/adresyZeme";
import VoiceFillModal from "@/components/crm/VoiceFillModal";
import GmailComposerModal from "@/components/crm/GmailComposerModal";
import AresAutocomplete from "@/components/crm/AresAutocomplete";
import AIEmailDraftModal from "@/components/posta/AIEmailDraftModal";
import WhatsAppSender from "@/components/crm/WhatsAppSender";
import NaborFolderPanel from "@/components/nabor/NaborFolderPanel";
import { naborStageLabel, naborStageColor } from "@/lib/naborFaze";
import VoiceRecorderWidget from "@/components/VoiceRecorderWidget";
import ClientDealsTab from "@/components/crm/ClientDealsTab";
import AIWatchdogOpportunities from "@/components/crm/AIWatchdogOpportunities";
import ContractsTab from "@/components/crm/tabs/ContractsTab";
import MajetekTab from "@/components/crm/tabs/MajetekTab";
import RozvahaGraf from "@/components/crm/RozvahaGraf";
import HypotekaDotaznikKarta from "@/components/crm/hypoteka/HypotekaDotaznikKarta";
import KlientskeFormularKarta from "@/components/crm/formulare/KlientskeFormularKarta";
import { rozvaha, uctyZAnalyzy } from "@/lib/crm/rozvaha";
/* KARTA = ANALÝZA (A3.3): účty se dočítají z analýzy a rozvaha se kreslí
   v MĚNĚ DAT analýzy, ne v měně účtu — data připravená stejně jako na
   stránce analýzy (lib/analyzaV2/menaDat.js). */
import { menaOrganizace, pripravAnalyzu } from "@/lib/analyzaV2/menaDat";
import { sJednotkou } from "@/lib/castka";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { getCategoryStyle, normalizeCategory } from "@/lib/categoryStyle";
import { CATEGORY_STYLES as ELITE_CATS } from "@/lib/eliteCategoryStyle";
import { runContactSave, buildContactSavePatch } from "@/lib/clientFolderSave";
import { registerFolderGuard, clearFolderGuard } from "@/lib/clientFolderGuard";
import { navigateOverlaySafe } from "@/lib/paletteNav";
/* Telefon → odkaz. Vytáčení i WhatsApp jdou přes jeden modul, který doplní
 * předvolbu podle ZEMĚ PORADCE (nikdy podle jazyka rozhraní) a u nesmyslného
 * čísla vrátí prázdno, takže se tlačítko vůbec nenabídne. */
import { odkazTel, odkazWhatsApp } from "@/lib/telefonCislo";
import { pluralJazyka, textJazyka } from "@/lib/i18n/textMimoReact";
import { TL, localeUI } from "@/lib/prekladyUI";
import { zapnuto } from "@/lib/funkce"; // aiTlacitka — placená AI (diktování, AI e-mail, Diskuze)

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmt = (n) => new Intl.NumberFormat("cs-CZ").format(Math.round(n || 0));

const daysBetween = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
};

// Zmenší a zkomprimuje fotku v prohlížeči PŘED uploadem — ať „nežere server".
// Cíl: delší strana max ~512 px, JPEG q~0.72 → typicky 20–60 KB místo několika MB.
// GIF (může být animovaný) i případné selhání → vrátíme původní soubor beze změny.
async function compressImageFile(file, maxDim = 512, quality = 0.72) {
  if (!file || file.type === "image/gif") return file;
  try {
    const dataUrl = await new Promise((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(fr.result);
      fr.onerror = rej;
      fr.readAsDataURL(file);
    });
    const img = await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = rej;
      im.src = dataUrl;
    });
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(img, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob || blob.size >= file.size) return file;  // už malé JPEG → nekomprimuj zbytečně
    const base = (file.name || "foto").replace(/\.[^.]+$/, "");
    return new File([blob], `${base}.jpg`, { type: "image/jpeg" });
  } catch {
    return file;  // cokoli selže → pošli originál (upload nepřerušíme)
  }
}

/**
 * Raynet-inspired 3-column client detail.
 * Layout (desktop, IMG_9610 / IMG_9611):
 *   LEFT      ─ Contact essentials (jméno, telefon, email, adresa, kategorie, fáze)
 *   CENTER    ─ Aktivity, termíny fází, vazby na další klienty (rodina), produkty, poznámky
 *   RIGHT     ─ Finanční souhrn (BJ, AUM, příští aktivita, posl. aktivita)
 *
 * On mobile (<768px) the columns stack vertically with the financial summary first
 * for "focus mode" (Jméno · Fáze · BJ · Akce).
 */
// Jednotná šířka popisků ve formuláři karty (Adresy + Kontakty) → zarovnané levé hrany inputů.
const LW = 64;

export default function ClientDetailRaynet({ contact: c, onClose, onPatch, onDelete, onSign, koef = 87.5, fullScreen = false, initialTab, navToken }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(initForm(c));
  // baseline = poslední uložený / načtený stav formuláře. Proti němu se počítá
  // „dirty" (neuložené změny) a z něj se staví DIRTY-FIELD PATCH (posílá se jen to,
  // co se skutečně změnilo → netknutá pole zůstanou beze změny, žádný přepis defaultem).
  const [baseline, setBaseline] = useState(() => initForm(c));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false); // true → poslední save selhal (nabídni retry)
  const [savedOk, setSavedOk] = useState(false);     // true → poslední save prošel (ukaž „Uloženo ✓")
  const [confirmClose, setConfirmClose] = useState(false); // dirty-guard dialog při zavírání
  // Foto klienta — klik na avatar nahraje (stejný endpoint jako boční panel). photoUrl drží lokální override.
  const photoRef = useRef(null);
  const [photoUrl, setPhotoUrl] = useState(c.photo_url || "");
  const [photoUploading, setPhotoUploading] = useState(false);
  useEffect(() => { setPhotoUrl(c.photo_url || ""); }, [c.photo_url]);
  const handlePhotoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !c?.id) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(TL("Foto je moc velké (max 5 MB)", lang)); return; }
    setPhotoUploading(true);
    try {
      const toUpload = await compressImageFile(file);  // zmenší v prohlížeči → lehké pro server
      const fd = new FormData();
      fd.append("file", toUpload);
      const r = await api.post(`/api/crm/contacts/${c.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoUrl(r.data.photo_url);
      onPatch?.({ photo_url: r.data.photo_url });
      toast.success(TL("Foto nahráno", lang));
    } catch (err) {
      toast.error((TL("Chyba uploadu: ", lang)) + (err?.response?.data?.detail || ""));
    } finally { setPhotoUploading(false); }
  };
  const [contracts, setContracts] = useState([]);
  const [contractsError, setContractsError] = useState(false); // chyba načtení ≠ „žádné smlouvy"
  // Nefinanční majetek (byt, auto, pole, lesy, zlato, podíl ve firmě).
  // Stejné pravidlo jako u smluv: chyba načtení ≠ „klient nic nemá".
  const [assets, setAssets] = useState([]);
  const [assetsError, setAssetsError] = useState(false);
  /* Uložená analýza klienta (financial_maps.data.v2) — kvůli BANKOVNÍM ÚČTŮM
     a měně dat. Účty se do složky nepropisují (viz lib/crm/rozvaha.js →
     uctyZAnalyzy), takže bez analýzy by karta ukázala čisté jmění bez nich.
     null = klient analýzu nemá; chyba načtení je zvlášť (≠ „nemá“). */
  const [analyza, setAnalyza] = useState(null);
  const [analyzaError, setAnalyzaError] = useState(false);
  // Náborový kontakt otevírá složku rovnou na záložce Nábor — jinak by poradce
  // koukal na klientskou kartu a nepoznal, že jde o kandidáta.
  const isRecruit = !!c.is_recruit;
  const [activeTab, setActiveTab] = useState(initialTab || (c.is_recruit ? "nabor" : "karta"));
  /* Záložka Diskuze = AI chat o klientovi (placená AI) — jen se zapnutým
   * přepínačem aiTlacitka (lib/funkce.js). */
  const visibleTabs = useMemo(
    () => (isRecruit
      ? [RECRUIT_TAB, ...TABS.filter((t) => !RECRUIT_HIDDEN_TABS.includes(t.key))]
      : TABS).filter((t) => t.key !== "diskuze" || zapnuto("aiTlacitka")),
    [isRecruit],
  );
  // Kdyby někdo vyžádal záložku, která pro tenhle typ kontaktu neexistuje
  // (např. odkaz na Smlouvy u kandidáta), spadni na první dostupnou — ne na prázdno.
  useEffect(() => {
    if (!visibleTabs.some((t) => t.key === activeTab)) setActiveTab(visibleTabs[0].key);
  }, [visibleTabs, activeTab]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [tags, setTags] = useState(Array.isArray(c.tags) ? c.tags : []);
  const [editorState, setEditorState] = useState({ open: false, contract: null });
  const [voiceFillOpen, setVoiceFillOpen] = useState(false);
  const [gmailComposerOpen, setGmailComposerOpen] = useState(false);
  const [aiEmailOpen, setAiEmailOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const { user } = useAuth();
  const { lang, currency, t } = useLanguage();
  // Peněžní formát UVNITŘ komponenty (vidí currency). Přepočet se NEDĚLÁ —
  // měna jde ze země tenantu, hodnota se nemění (lib/tenantMena.js).
  const fmtMoney = (castka) => `${fmt(Number(castka) || 0)} ${currency.symbol}`;
  /* BJ × koeficient pozice jen u korunového tenanta; u SK hláška místo částky
   * (rozhodnutí majitele 19. 10. 2026, CRM-SK-01 — dřív „26 250 €“). */
  const penizeZBj = (bj) => {
    const v = bjNaPenize(Number(bj) || 0, koef, currency?.code);
    return v == null ? "—" : fmtMoney(v);
  };
  /* ARES = český rejstřík → jen u CZ tenanta (CRM-SK-17). */
  const aresDostupny = useSyncExternalStore(odebirejZemi, zemeTenanta, zemeTenanta) === "CZ";
  /* ROZVAHA = TATÁŽ ČÍSLA JAKO ANALÝZA (A3.3, audit 3. 10. 2026).
   * Karta počítala čisté jmění bez bankovních účtů (demo 2 971 000 proti
   * 3 196 000 v analýze). Teď: smlouvy + majetek ze složky + účty z analýzy,
   * analýza připravená přesně jako na své stránce (řádek se starým kurzem
   * se nezapočítá ani tady). Přepočítá se, jen když se změní vstupy. */
  const menaOrg = menaOrganizace(user);
  const pripravena = useMemo(() => pripravAnalyzu(analyza, menaOrg), [analyza, menaOrg]);
  const rozvahaKlienta = useMemo(
    () => rozvaha(contracts, assets, { ucty: pripravena ? uctyZAnalyzy(pripravena.d) : [] }),
    [contracts, assets, pripravena],
  );
  /* Když se nenačetly smlouvy, majetek nebo analýza, rozvaha by vyšla
     NEÚPLNÁ (bez dluhů klidně i vyšší) a tvářila by se jako celé jmění.
     Raději žádný graf — chyby smluv a majetku hlásí jejich záložky. */
  const rozvahaZobrazit = contractsError || assetsError || analyzaError ? null : rozvahaKlienta;
  /* MĚNA DAT, NE MĚNA ÚČTU: čísla rozvahy jsou z analýzy, takže nesou její
     měnu (stará korunová analýza u slovenského účtu = Kč, ne €). Klient bez
     analýzy → měna účtu jako dřív. Formát čísla podle jazyka, ne natvrdo
     česky. Částky BJ / provizí na Kartě zůstávají na `fmtMoney` (měna účtu). */
  const symbolDat = pripravena ? pripravena.mena.symbol : currency.symbol;
  const fmtMoneyDat = (castka) =>
    sJednotkou(new Intl.NumberFormat(currency.locale || localeUI(lang)).format(Math.round(Number(castka) || 0)), symbolDat);

  useEffect(() => {
    const fresh = initForm(c);
    setForm(fresh);
    setBaseline(fresh);          // nový klient → čistý baseline (dirty=false)
    setSaveError(false);
    setSavedOk(false);
    setConfirmClose(false);
    setTags(Array.isArray(c.tags) ? c.tags : []);
    // Most Návrh → Smlouva: když přicházíme z návrhu s předvyplněním pro tohoto
    // klienta, rovnou otevři záložku Smlouvy + editor (předvyplnění čte ContractEditor).
    let prefilled = false;
    try {
      const raw = localStorage.getItem("beplan_contract_prefill");
      if (raw) { const p = JSON.parse(raw); if (p.contact_id === c.id) prefilled = true; }
    } catch { /* ignore */ }
    if (prefilled) { setActiveTab("smlouvy"); setEditorState({ open: true, contract: null }); }
    else setActiveTab(initialTab || (c.is_recruit ? "nabor" : "karta"));  // respektuj požadovanou záložku (navigační kontrakt)
  }, [c.id]); // eslint-disable-line

  // Když přijde nový požadavek na konkrétní záložku (stejný klient, jiná/stejná sekce) —
  // navigační kontrakt: přepni bez remountu. navToken se mění při každém požadavku,
  // takže i stejná sekce se dá vyžádat opakovaně (nikdy no-op).
  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [navToken, initialTab]);

  // dirtyRef drží aktuální „dirty" pro event handlery (Esc/guard), aby nevznikla
  // stale-closure — čte se až v čase události. confirmCloseRef zrcadlí otevřený dialog.
  const dirtyRef = useRef(false);
  const confirmCloseRef = useRef(false);
  useEffect(() => { confirmCloseRef.current = confirmClose; }, [confirmClose]);

  // Esc zavře kartu (jen když není otevřený vnořený modal — ten si Esc řeší sám).
  // Při neuložených změnách nezavírá tiše — otevře bezpečnostní dialog.
  useEffect(() => {
    const onEsc = (e) => {
      if (e.key !== "Escape") return;
      if (editorState.open || voiceFillOpen || gmailComposerOpen || aiEmailOpen || waOpen) return;
      if (confirmCloseRef.current) return; // dialog je otevřený → Esc řeší dialog, ne dvojité zavření
      requestClose();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
    // requestClose je stabilní useCallback (definovaný níže) — čte se přes closure až v čase Esc.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorState.open, voiceFillOpen, gmailComposerOpen, aiEmailOpen, waOpen, onClose]);

  const refreshContracts = async () => {
    try {
      const { data } = await api.get(`/api/contracts?contact_id=${c.id}`);
      setContracts(Array.isArray(data) ? data : []);
      setContractsError(false);
    } catch { setContractsError(true); }
  };

  const refreshAssets = async () => {
    try {
      const { data } = await api.get(`/api/assets?contact_id=${c.id}`);
      setAssets(Array.isArray(data) ? data : []);
      setAssetsError(false);
    } catch { setAssetsError(true); }
  };

  const refreshContactTags = async () => {
    // After voice recording, contact tags may have been auto-enriched by AI.
    try {
      const { data } = await api.get(`/api/crm/contacts/${c.id}`);
      if (Array.isArray(data?.tags)) setTags(data.tags);
    } catch { /* ignore */ }
  };

  // Load contracts whenever the open contact changes — feeds portfolio dlaždice + záložky.
  useEffect(() => {
    let cancelled = false;
    /* Analýza předchozího klienta se nesmí ani na okamžik sečíst se smlouvami
       nového — při přepnutí klienta se zahodí hned, ne až po načtení. */
    setAnalyza(null);
    setAnalyzaError(false);
    (async () => {
      try {
        const { data } = await api.get(`/api/contracts?contact_id=${c.id}`);
        if (!cancelled) { setContracts(Array.isArray(data) ? data : []); setContractsError(false); }
      } catch {
        // NESMÍ vypadat jako „klient nemá smlouvy" — to by poradce řekl klientovi nahlas.
        if (!cancelled) { setContractsError(true); setContracts([]); }
      }
      try {
        const { data } = await api.get(`/api/assets?contact_id=${c.id}`);
        if (!cancelled) { setAssets(Array.isArray(data) ? data : []); setAssetsError(false); }
      } catch {
        // Jako u smluv: prázdný seznam po chybě by poradce četl jako
        // „klient žádný majetek nemá" — a řekl by to klientovi nahlas.
        if (!cancelled) { setAssetsError(true); setAssets([]); }
      }
      try {
        const { data: fam } = await api.get(`/api/family/${c.id}`);
        if (!cancelled) setFamilyMembers(fam?.members || []);
      } catch {
        if (!cancelled) setFamilyMembers([]);
      }
      /* Analýza kvůli účtům a měně dat v rozvaze (A3.3). Bez analýzy vrací
         backend `{ data: null }` → klient analýzu nemá, to NENÍ chyba. */
      try {
        const { data: fm } = await api.get(`/api/crm/contacts/${c.id}/finmap`);
        const v2 = fm && typeof fm === "object" && fm.data && typeof fm.data === "object" ? fm.data.v2 : null;
        if (!cancelled) { setAnalyza(v2 && typeof v2 === "object" && !Array.isArray(v2) ? v2 : null); setAnalyzaError(false); }
      } catch {
        if (!cancelled) { setAnalyza(null); setAnalyzaError(true); }
      }
    })();

    // Refresh family when FamilyPicker links a new member
    const refresh = async () => {
      try {
        const { data: fam } = await api.get(`/api/family/${c.id}`);
        setFamilyMembers(fam?.members || []);
      } catch {}
    };
    window.addEventListener("family-changed", refresh);

    return () => {
      cancelled = true;
      window.removeEventListener("family-changed", refresh);
    };
  }, [c.id]);

  const stage = PIPELINE_STAGES.find((s) => s.id === c.stage);

  // DIRTY-FIELD PATCH: jen skutečně změněná pole proti baseline (žádný full-form
  // overwrite → netknutá pole se nepřepíšou, defaulty z initForm nepřepíšou prázdno).
  const dirtyPatch = useMemo(() => buildContactSavePatch(form, baseline), [form, baseline]);
  const dirty = Object.keys(dirtyPatch).length > 0;

  // Drž dirtyRef v synchronu (pro Esc handler) a jakmile uživatel znovu edituje,
  // zhasni „Uloženo ✓".
  useEffect(() => { dirtyRef.current = dirty; if (dirty) setSavedOk(false); }, [dirty]);

  const save = async () => {
    if (!dirty) return true; // nic ke změně → žádný zbytečný PUT
    setSaveError(false);
    const ok = await runContactSave({
      onPatch,
      patch: dirtyPatch,
      setSaving,
      onSuccess: () => {
        setBaseline(form);   // uloženo → nový baseline (dirty se vynuluje)
        setSavedOk(true);
        toast.success(TL("Uloženo", lang));
      },
    });
    // Rodič (ClientFolderHost) už zobrazil toast.error a re-thrownul; tady jen
    // zapneme chybový stav → v patičce se ukáže „Zkusit uložit znovu". Žádné
    // falešné „Uloženo" po selhání (onSuccess se při chybě nespustí).
    if (!ok) setSaveError(true);
    return ok;
  };

  // ── Bezpečné zavření (dirty-guard) — pokrývá VŠECHNY cesty ven ──
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  const proceedRef = useRef(null);   // akce, která se provede po potvrzení dialogu
  const closingRef = useRef(false);  // už zavíráme (po potvrzení) → Host se nemá ptát podruhé

  // Jádro: má-li se „zavřít / odejít" a jsou neuložené změny → dialog; jinak rovnou proveď.
  const requestGuardedClose = useCallback((proceed) => {
    if (dirtyRef.current && !closingRef.current) { proceedRef.current = proceed; setConfirmClose(true); }
    else proceed();
  }, []);

  // X / Esc / backdrop → zavřít složku (přes Hosta).
  const requestClose = useCallback(() => {
    requestGuardedClose(() => onCloseRef.current?.());
  }, [requestGuardedClose]);

  // Dialog: Uložit a pokračovat / Zahodit / Zůstat.
  const runProceed = async (mode) => {
    if (mode === "save") { const ok = await save(); if (!ok) return; } // save selhal → dialog zůstane, data neztracena
    const proceed = proceedRef.current; proceedRef.current = null;
    closingRef.current = true; // po dobu SYNCHRONNÍ teardown-kaskády se Host nemá ptát znovu
    setConfirmClose(false);
    try {
      proceed?.();
    } finally {
      // Kaskáda close eventů je synchronní → reset až po ní. Když teardown uspěl, složka se
      // odmountuje (ref zaniká, neškodí). Když re-dispatch přepnutí SELHAL (fetch klienta padl)
      // a složka zůstane, guard se znovu zapne → žádná trvalá ztráta ochrany.
      closingRef.current = false;
    }
  };
  const cancelClose = () => { proceedRef.current = null; setConfirmClose(false); };

  // Guard pro EXTERNÍ cesty ven (GlobalTabBar „×", „Zavřít vše", Cmd+K/navigace, přepnutí
  // klienta, aktivace sekce): všechny jdou přes ClientFolderHost, který je jediný vlastník
  // overlaye. Zaregistrujeme se do sdíleného registru — Host se před teardownem zeptá
  // (folderIsDirty) a při neuložených změnách spustí náš dialog (requestGuardedClose), takže
  // se rozepsané změny neztratí. (Registr žije mimo React → čte dirtyRef živě.)
  useEffect(() => {
    const token = registerFolderGuard({ isDirty: () => dirtyRef.current && !closingRef.current, requestClose: requestGuardedClose });
    return () => clearFolderGuard(token); // identity-checked → odcházející složka nesmaže guard nové
  }, [requestGuardedClose]);

  // HOTFIX pravdivosti (2026-07): dřív se tu bez skutečné aktivity dosazoval updated_at.
  // updated_at je čas POSLEDNÍ ZMĚNY KARTY (stačilo přepsat telefon a uložit), ne poslední
  // zrealizovaná aktivita s klientem. Karta pak tvrdila „Poslední aktivita před 2 d“, i když
  // se s klientem nikdo měsíce nebavil — a poradce to takhle řekl nahlas. Bez skutečného
  // last_activity_at (backend ho zatím neplní, viz report) ukazujeme poctivě „—“.
  const lastActivityDays = c.last_activity_at ? daysBetween(c.last_activity_at) : null;
  const nextActivityDays = c.next_activity_at ? daysBetween(c.next_activity_at) : null;

  return (
    <>
      {!fullScreen && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={requestClose}
          className="fixed inset-0 bg-black/65 backdrop-blur-sm z-40"
        />
      )}
      <motion.aside
        initial={fullScreen ? { opacity: 0, y: 6 } : { x: "100%" }}
        animate={fullScreen ? { opacity: 1, y: 0 } : { x: 0 }}
        exit={fullScreen ? { opacity: 0 } : { x: "100%" }}
        transition={{ type: "spring", damping: 32, stiffness: 320 }}
        className={fullScreen
          ? "ink-board apple-skin w-full h-full flex flex-col"
          : "ink-board apple-skin fixed right-0 top-0 bottom-0 w-full md:w-[960px] z-50 flex flex-col"}
        style={{
          background: "var(--bg)",
          color: "var(--ink)",
          ...(fullScreen ? {} : {
            borderLeft: "1px solid var(--line)",
            boxShadow: "var(--shadow-float)",
          }),
        }}
        data-testid="client-detail-panel"
      >
        {/* NÁBOROVÁ SLOŽKA — na první pohled musí být poznat, že tohle není klient.
            Dřív se kandidát otevíral ve zcela stejné složce jako klient a nic to neřeklo. */}
        {isRecruit && (
          <div
            className="flex items-center gap-2 flex-wrap px-4 md:px-5 py-2"
            style={{ background: "linear-gradient(90deg, #6D48B8, #4A2E85)", color: "#FFFFFF" }}
            data-testid="recruit-ribbon"
          >
            <span className="text-popisek uppercase tracking-[0.22em] font-bold px-1.5 py-0.5 rounded" style={{ background: "rgba(255,255,255,0.22)" }}>
              {t("slozkaKlienta.nabor")}
            </span>
            <span className="text-[12px] font-semibold">
              {TL("Kandidát do týmu, ne klient", lang)}
            </span>
            <span
              className="text-drobny font-bold px-2 py-0.5 rounded-full"
              style={{ background: "rgba(255,255,255,0.18)", outline: `1px solid ${naborStageColor(c.recruit_stage)}` }}
              data-testid="recruit-ribbon-stage"
            >
              {naborStageLabel(c.recruit_stage, lang)}
            </span>
            {c.recruit_meta?.firma && (
              <span className="text-drobny inline-flex items-center gap-1" style={{ color: "rgba(255,255,255,0.85)" }}>
                <Building2 size={10} /> {c.recruit_meta.firma}
              </span>
            )}
          </div>
        )}

        {/* Glass sticky hero header — VIP/A/B/C tier ribbon + KPI cards (v3.3 Adviser Hub Elite-2026 inspired) */}
        {(() => {
          const catStyle = getCategoryStyle(c);
          const catKey = normalizeCategory(c.category);
          const eliteCat = ELITE_CATS[catKey] || ELITE_CATS.C;
          const aum = Number(c.signed_bj) || 0;
          const meetingsCount = Number(c.meetings_count) || 0;
          const familyCount = Array.isArray(familyMembers) ? familyMembers.length : 0;
          const clientSince = c.created_at ? new Date(c.created_at) : null;
          const yearsSince = clientSince ? Math.max(0, new Date().getFullYear() - clientSince.getFullYear()) : 0;
          return (
            <div className="relative">
              {/* Elite kit — dekorativní gradient banner dle kategorie (VIP zlato / A platina / B bronz / C neutrál).
                  Hlavička (avatar) se překrývá přes jeho spodní hranu. */}
              {/* Ph4B: slim kategoriální akcent (dřív h-8=32px dekorativní pruh + duplicitní
                  hvězdičkový VIP nápis). VIP se v hlavičce ukazuje badgem + kategorií ve formuláři,
                  takže velký pruh i nápis jsou nadbytečné, zmenšeno na 10px, méně vertikální režie. */}
              <div
                className={`h-2.5 relative overflow-hidden bg-gradient-to-r ${eliteCat.gradientBanner}`}
                data-testid="category-banner"
              >
                {/* jemný lesk nahoře — drahokamový efekt */}
                <div className="absolute inset-0" aria-hidden style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.35), rgba(255,255,255,0) 65%)" }} />
              </div>
              <div className="glass-bar z-10 px-4 md:px-6 py-2 flex-shrink-0">
                <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${catStyle.solid}88 50%, transparent)` }} />
                {/* Pod 640 px pod sebou (CRM-05): jméno + čipy nahoře, akce pod nimi.
                    Vedle sebe akce sežraly celou šířku a jméno zmizelo. */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1 w-full sm:w-auto">
                    {/* Avatar 48px — překrývá spodní hranu gradient banneru, ring-4 „white“ efekt přes boxShadow */}
                    <div className="relative flex-shrink-0 self-start -mt-2">
                      <input ref={photoRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={handlePhotoFile} className="hidden" data-testid="detail-photo-input" />
                      <button
                        type="button"
                        onClick={() => photoRef.current?.click()}
                        disabled={photoUploading}
                        className="group relative w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center"
                        style={{
                          background: photoUrl ? "var(--surface)" : catStyle.bgSoft,
                          boxShadow: `0 0 0 3px var(--surface), 0 0 0 4.5px ${catStyle.ringStrong}`,
                        }}
                        title={photoUrl ? (TL("Změnit foto klienta", lang)) : (TL("Nahrát foto klienta", lang))}
                        data-testid="detail-photo-btn"
                      >
                        {photoUrl ? (
                          <img src={photoUrl} alt={c.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="font-display text-[16px] font-semibold" style={{ color: catStyle.text }}>
                            {(c.name?.[0] || "?").toUpperCase()}{(c.surname?.[0] || "").toUpperCase()}
                          </span>
                        )}
                        <span className="absolute inset-0 flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100" style={{ background: "rgba(0,0,0,0.45)", color: "#fff" }}>
                          {photoUploading ? <span className="text-drobny">…</span> : <Camera size={13} />}
                        </span>
                      </button>
                      <span
                        className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-drobny font-bold"
                        style={{ color: "#FFFFFF", background: catStyle.bg, outline: "1.5px solid var(--surface)", outlineOffset: "-0.5px" }}
                        title={`${catStyle.label} klient`}
                        data-testid="hero-tier-star"
                      >
                        {catStyle.symbol}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="font-display text-[18px] md:text-[20px] line-clamp-2 sm:line-clamp-1 break-words min-w-0" style={{ fontFamily: "var(--font-display)", color: "var(--ink)", fontWeight: 600, letterSpacing: "-0.02em", lineHeight: 1.1 }} data-testid="detail-client-name">
                          {c.name}{c.surname ? ` ${c.surname}` : ""}
                        </h2>
                        {/* Category Pill — Elite kit vzor (drahokamový gradient chip) */}
                        <span
                          className={`text-popisek uppercase tracking-[0.14em] font-bold px-2 h-5 inline-flex items-center gap-1 rounded-full flex-shrink-0 ${eliteCat.chip}`}
                          data-testid="hero-tier-badge"
                        >
                          {eliteCat.symbol} {eliteCat.label}
                        </span>
                        {stage && (
                          <span
                            className="text-popisek px-1.5 py-0.5 rounded-full uppercase tracking-wider font-semibold flex-shrink-0"
                            style={{
                              background: `${stage.color}1A`,
                              color: stage.color,
                              outline: `1px solid ${stage.color}44`,
                              outlineOffset: "-1px",
                            }}
                            data-testid="detail-stage-badge"
                          >
                            {stage.label}
                          </span>
                        )}
                        {/* Inline KPIs — match Raynet density */}
                        {/* NENÍ to AUM (CRM-06): podepsané BJ × koeficient pozice = provizní
                            odhad, ne majetek klienta. Kompaktně v locale a měně tenanta
                            („875 mil. Kč"); bez kurzu (SK) jen BJ. */}
                        {aum > 0 && (
                          <span className="text-drobny inline-flex items-center gap-1 ml-1" data-testid="kpi-aum" style={{ color: "var(--ink-muted)" }} title={TL("Podepsané BJ × koeficient pozice — odhad, ne majetek klienta", lang)}>
                            <Wallet size={10} style={{ color: "var(--money-text)" }} />
                            {TL("Podepsáno", lang)} ≈{" "}
                            <span className="font-semibold mono" style={{ color: "var(--money-text)" }}>{(() => {
                              const v = bjNaPenize(aum, koef, currency?.code);
                              return v == null
                                ? `${new Intl.NumberFormat(currency?.locale || "cs-CZ").format(aum)} BJ`
                                : new Intl.NumberFormat(currency?.locale || "cs-CZ", { style: "currency", currency: currency.code, notation: "compact", maximumFractionDigits: 1 }).format(v);
                            })()}</span>
                          </span>
                        )}
                        {familyCount > 0 && (
                          <span className="text-drobny inline-flex items-center gap-1" data-testid="kpi-family" style={{ color: "var(--ink-muted)" }}>
                            <Users size={10} style={{ color: "#7FA62B" }} />
                            <span className="font-semibold" style={{ color: "var(--ink)" }}>{familyCount}</span>
                          </span>
                        )}
                        {meetingsCount > 0 && (
                          <span className="text-drobny inline-flex items-center gap-1" data-testid="kpi-meetings" style={{ color: "var(--ink-muted)" }}>
                            <Calendar size={10} style={{ color: "#6D48B8" }} />
                            <span className="font-semibold" style={{ color: "var(--ink)" }}>{meetingsCount}</span>
                          </span>
                        )}
                      </div>
                      <div className="text-drobny mt-0.5 flex items-center gap-2.5 flex-wrap" style={{ color: "var(--muted)" }}>
                        {c.phone && <span className="inline-flex items-center gap-1"><Phone size={10} />{c.phone}</span>}
                        {c.email && <span className="inline-flex items-center gap-1"><Mail size={10} />{c.email}</span>}
                        {c.city && <span className="inline-flex items-center gap-1"><MapPin size={10} />{c.city}</span>}
                        {clientSince && (
                          <span className="inline-flex items-center gap-1">
                            {/* Tvar roku podle počtu z Intl.PluralRules (pluralJazyka),
                                ne z českého pravidla 1 / 2–4 / 5+. */}
                            <Clock size={10} />Klient {yearsSince > 0 ? `${yearsSince} ${pluralJazyka(lang, "nastroje.pdVety.rok", yearsSince)}` : "od " + clientSince.toLocaleDateString(localeUI(), { month: "short", year: "numeric" })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Compact action strip — right side (na mobilu řádek pod jménem, zalamuje se) */}
                  <div className="flex items-center gap-1 flex-wrap sm:flex-nowrap flex-shrink-0" data-testid="detail-action-strip">
                    {odkazTel(c.phone) && (
                      <a
                        href={odkazTel(c.phone)}
                        title={TL("Zavolat", lang)}
                        className="p-1.5 rounded-md transition-colors hover:bg-black/5"
                        style={{ color: "var(--ink)" }}
                        data-testid="quick-call"
                      >
                        <Phone size={14} />
                      </a>
                    )}
                    {c.phone && (
                      <button
                        onClick={() => setWaOpen(true)}
                        title={TL("Poslat na WhatsApp (1 klik, šablony)", lang)}
                        data-testid="quick-whatsapp"
                        className="p-1.5 rounded-md transition-colors hover:bg-black/5"
                        style={{ color: "#25D366" }}
                      >
                        <MessageCircle size={14} />
                      </button>
                    )}
                    {c.email && (
                      <button
                        onClick={() => setGmailComposerOpen(true)}
                        title={TL("Odeslat e-mail z Gmailu", lang)}
                        data-testid="quick-email"
                        className="p-1.5 rounded-md transition-colors hover:bg-black/5"
                        style={{ color: "var(--ink)" }}
                      >
                        <Mail size={14} />
                      </button>
                    )}
                    {c.email && zapnuto("aiTlacitka") && (
                      <button
                        onClick={() => setAiEmailOpen(true)}
                        title={TL("AI Email — hlasový/textový příkaz vygeneruje email", lang)}
                        data-testid="quick-ai-email"
                        className="flex items-center gap-1 px-2.5 py-1 rounded-full font-semibold text-popisek uppercase tracking-wide transition-colors"
                        style={{ background: "var(--accent-soft)", color: "var(--accent)", border: "1px solid var(--accent)" }}
                      >
                        <Sparkles size={11} />
                        AI Email
                      </button>
                    )}
                    {/* ⚠️ DUPLICITA K ROZHODNUTÍ: pár řádků výš už je WhatsApp
                        tlačítko (quick-whatsapp), které otevře WhatsAppSender
                        s šablonami. Tohle je holý odkaz bez textu. Dvě zelené
                        bubliny vedle sebe matou — jedna z nich má zmizet, ale
                        která, rozhodne majitel. Do té doby aspoň nevede na cizí
                        číslo: dřív se tu jen odmazaly nečíslice, takže uložené
                        devítimístné číslo šlo na wa.me bez předvolby. */}
                    {/* DRUHÉ WHATSAPP TLAČÍTKO ODEBRÁNO na přání majitele
                      * (24. 9. 2026): „vpravo nahoře jsou 2 okýnka na WhatsApp,
                      * stačí to jedno". Byla vedle sebe dvě, se stejnou ikonou
                      * i barvou — poradce nepoznal, které je které. Zůstává
                      * `quick-whatsapp`, protože otevře šablony; tohle byl jen
                      * holý odkaz na wa.me. */}
                    {!isRecruit && (
                    <button
                      // Analýza je MIMO AppLayout → odmount složky by obešel dirty-guard
                      // a rozepsaná karta by se tiše ztratila. Proto přes guard.
                      onClick={() => requestGuardedClose(() => navigate(`/crm/clients/${c.id}/analyza`))}
                      title={TL("Finanční analýza — řízená 45min schůzka", lang)}
                      data-testid="client-card-analyza"
                      className="flex items-center gap-1 px-2.5 py-1 rounded-full font-semibold text-popisek uppercase tracking-wide transition-colors"
                      style={{ background: "var(--surface)", color: "var(--accent)", border: "1px solid var(--accent)" }}
                    >
                      <FileCheck size={11} />
                      {t("slozkaKlienta.analyza")}
                    </button>
                    )}
                    <button
                      onClick={() => {
                        // QA-A4 — open NewActivityModal v Diáři s předvyplněným klientem.
                        const params = new URLSearchParams({
                          newActivity: "1",
                          contact_id: c.id || "",
                          contact_name: c.name || "",
                        });
                        // /diar je UVNITŘ AppLayout → bez zavření overlaye by Diář naskočil
                        // POD složku a klik by vypadal, že nic neudělal. + dirty-guard.
                        requestGuardedClose(() => navigateOverlaySafe(navigate, `/diar?${params.toString()}`));
                      }}
                      title={TL("Naplánovat aktivitu (úkol, schůzka, telefonát…)", lang)}
                      data-testid="client-card-new-activity"
                      className="flex items-center gap-1 px-2.5 py-1 rounded-full font-semibold text-popisek uppercase tracking-wide transition-colors"
                      style={{ background: "var(--surface)", color: "var(--ink)", border: "1px solid var(--line)" }}
                    >
                      <Plus size={11} />
                      {t("slozkaKlienta.aktivita")}
                    </button>
                    {/* Diktování do složky a hlasová poznámka volají placenou AI
                      * (Whisper + LLM) — jen se zapnutým přepínačem aiTlacitka. */}
                    {zapnuto("aiTlacitka") && (
                    <button
                      onClick={() => setVoiceFillOpen(true)}
                      title="AI Voice-Fill"
                      data-testid="voice-fill-open"
                      className="p-1.5 rounded-md transition-colors hover:bg-orange-50"
                      style={{ color: "var(--accent)" }}
                    >
                      <Sparkles size={14} />
                    </button>
                    )}
                    {zapnuto("aiTlacitka") && (
                    <VoiceRecorderWidget
                      inline
                      forcedContactId={c.id}
                      forcedContactName={`${c.name || ""} ${c.surname || ""}`.trim()}
                      onComplete={() => { refreshContracts(); refreshContactTags(); }}
                    />
                    )}
                    {/* Zelené tlačítko „Podepsat" ODEBRÁNO na přání majitele
                      * (24. 9. 2026): „v pravo nahoře podepsat je k ničemu".
                      * Posouvalo klienta do fáze „podepsáno" jedním kliknutím
                      * z hlavičky, mimo jakýkoliv obchod — vedle toho je
                      * pipeline i karta obchodu, kde se fáze mění v kontextu.
                      * `onSign` zůstává v props: volá ho pipeline. */}
                    <button onClick={requestClose} className="p-1.5 rounded-md transition-colors hover:bg-black/5" style={{ color: "var(--muted)" }} data-testid="detail-close" aria-label={TL("Zavřít", lang)}>
                      <X size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* KPI proužek pod hero ODEBRÁN (4. 7. 2026, přání Čunga: složka byla moc vysoká,
            čísla nedávala smysl). Rychlé KPI jsou v hero chipsech a v pravém finančním sloupci
            (Raynet-hutně, ať je vidět obsah bez dvojitého scrollu). */}

        {/* Tab navigation — Apple-style horizontal pills */}
        <ClientTabs activeTab={activeTab} setActiveTab={setActiveTab} tabs={visibleTabs} />

        {/* Scrollable tab content area */}
        <div className="flex-1 min-h-0 overflow-y-auto" data-testid="client-detail-scroll">
        {/* Tab content */}
        {activeTab === "nabor" && isRecruit && (
          <NaborFolderPanel contact={c} onPatch={onPatch} lang={lang} />
        )}
        {activeTab === "karta" && (
        <div className="px-4 md:px-5 py-3" data-testid="tab-karta">
          {/* RAYNET 1:1 — 3-column dense layout (4-5-3) */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2">

          {/* ───────────────────── LEFT col (4/12) — Základní údaje + Kontaktní osoby + Propojení + Poznámka ───────────────────── */}
          <div className="md:col-span-4 space-y-1.5">

            <RaynetSection title={TL("Základní údaje", lang)} icon={IdCard} accent="var(--accent)" extra={aresDostupny && (
              <span className="text-popisek font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
                style={{ background: "var(--accent-soft)", color: "var(--accent)", border: "1px solid var(--accent)" }}
                title={TL("Doplní se z rejstříku ARES", lang)}
              >
                {TL("z rejstříku", lang)}
              </span>
            )}>
              <RaynetField label="IČO / firma">
                <AresAutocomplete
                  value={form.company || form.ico || ""}
                  onChange={(v) => setForm({ ...form, company: v })}
                  testid="detail-ares-search"
                  placeholder={TL(aresDostupny ? "IČO nebo název firmy — doplní se samo…" : "IČO nebo název firmy…", lang)}
                  onPick={(entity) => {
                    // Auto-fill z ARES — pošli JEN ARES pole (partial patch, ne celý
                    // formulář → žádný full-form overwrite; onPatch mimo setForm updater
                    // kvůli StrictMode dvojímu volání).
                    const addr = entity.address || {};
                    const patch = {
                      ico: entity.ico || form.ico,
                      company: entity.name || form.company,
                      is_physical_person: !!entity.is_person,
                      street: addr.street || form.street,
                      city: addr.city || form.city,
                      psc: addr.psc || form.psc,
                      region: addr.region || form.region,
                    };
                    // Fyzická osoba — rozděl jméno na name/surname (jen když jméno chybí)
                    if (entity.is_person && entity.name && !form.name) {
                      const parts = entity.name.trim().split(/\s+/);
                      patch.name = parts[0] || "";
                      patch.surname = parts.slice(1).join(" ") || "";
                    }
                    setForm((f) => ({ ...f, ...patch }));
                    (async () => {
                      try {
                        await onPatch(patch);
                        setBaseline((b) => ({ ...b, ...patch })); // uloženo → baseline zohledni
                      } catch { /* onPatch shows toast */ }
                    })();
                    toast.success(`${TL("Načteno z ARES", lang)} — ${entity.name}`);
                  }}
                />
              </RaynetField>
              {/* Oddělené pole jméno + příjmení — spojené pole nešlo opravit
                  (split přes mezeru bránil napsat mezeru / prohodit pořadí). */}
              <RaynetField label={TL("Jméno", lang)}>
                <InputTight value={form.name || ""}
                  onChange={(v) => setForm({ ...form, name: v })}
                  testid="detail-name" placeholder={TL("Jan", lang)} />
              </RaynetField>
              <RaynetField label={TL("Příjmení", lang)}>
                <InputTight value={form.surname || ""}
                  onChange={(v) => setForm({ ...form, surname: v })}
                  testid="detail-surname" placeholder={t("slozkaKlienta.prijmeniPriklad")} />
              </RaynetField>
              <RaynetField label="Stav">
                <SelectTight
                  value={form.status || "active"}
                  onChange={(v) => setForm({ ...form, status: v })}
                  testid="detail-status"
                  options={[
                    { v: "active",   label: TL("Aktuální", lang) },
                    { v: "potential",label: TL("Potenciální", lang) },
                    { v: "inactive", label: TL("Neaktivní", lang) },
                    { v: "lost",     label: TL("Ztracený", lang) },
                  ]}
                />
              </RaynetField>
              <RaynetField label={t("slozkaKlienta.vlastnik")}>
                {/* Vlastník = přihlášený poradce. Backend vrací jen kontakty s
                    owner_id == aktuální uživatel (backend/routes/crm.py), takže hodnota je
                    pravdivá, ale MĚNIT ji z karty nejde — dřív tu byl editovatelný input
                    s onChange={() => {}}, do kterého šlo psát a systém to zahodil.
                    Proto readOnly (viz InputTight). */}
                <InputTight
                  value={user?.name || ""}
                  readOnly
                  testid="detail-owner"
                  title={TL("Vlastníka karty nelze měnit", lang)}
                  placeholder={user?.name || (TL("Poradce", lang))}
                />
              </RaynetField>
              <RaynetField label={TL("Vztah", lang)}>
                <SelectTight
                  value={form.relation || "client"}
                  onChange={(v) => setForm({ ...form, relation: v })}
                  options={[
                    { v: "client",    label: t("slozkaKlienta.vztahKlient") },
                    { v: "prospect",  label: TL("Zájemce", lang) },
                    { v: "partner",   label: t("slozkaKlienta.vztahPartner") },
                    { v: "supplier",  label: TL("Dodavatel", lang) },
                    { v: "other",     label: TL("Jiný", lang) },
                  ]}
                />
              </RaynetField>
              <RaynetField label={TL("Kategorie", lang)}>
                <div className="flex gap-1 w-full">
                  {["VIP", "A", "B", "C"].map((k) => (
                    <button
                      key={k}
                      onClick={() => setForm({ ...form, category: k })}
                      className="flex-1 py-0.5 rounded text-drobny font-bold transition-colors"
                      style={form.category === k
                        ? { background: k === "VIP" ? "var(--accent)" : k === "A" ? "#D69412" : k === "B" ? "#7FA62B" : "#C74E22", /* na jantaru a olivě bílá nedrží (2,6 a 2,9:1) → tmavý text */ color: k === "VIP" ? "var(--on-accent)" : k === "A" || k === "B" ? "var(--ink-na-barve)" : "#FFFFFF", border: "1px solid transparent" }
                        : { background: "var(--surface)", color: "var(--muted)", border: "1px solid var(--line)" }}
                      data-testid={`category-${k}`}
                    >
                      {k}
                    </button>
                  ))}
                </div>
              </RaynetField>
              <RaynetField label="Rating">
                <RatingStars value={form.rating || 0} onChange={(v) => setForm({ ...form, rating: v })} />
              </RaynetField>
              <RaynetField label={t("slozkaKlienta.zdrojKontaktu")}>
                <SelectTight
                  value={form.source || ""}
                  onChange={(v) => setForm({ ...form, source: v })}
                  /* Jeden číselník CONTACT_SOURCES (CRM-04). Hodnota, kterou číselník
                     nezná (leadovač, trychtýř, starý import), se ukáže jako vlastní
                     volba s lidským popisem — dřív „—", jako by zdroj nebyl zadán. */
                  options={[
                    { v: "", label: "—" },
                    ...CONTACT_SOURCES.map((z) => ({ v: z.id, label: TL(z.label, lang) })),
                    ...(form.source && !CONTACT_SOURCES.some((z) => z.id === form.source)
                      ? [{ v: form.source, label: popisZdroje(form.source).popis }]
                      : []),
                  ]}
                />
              </RaynetField>
              <RaynetField label={TL("Obor", lang)}>
                <InputTight value={form.industry || ""} onChange={(v) => setForm({ ...form, industry: v })} placeholder={TL("OSVČ, IT, stavebnictví…", lang)} />
              </RaynetField>
            </RaynetSection>

            <RaynetSection title={TL("Kontaktní osoby", lang)} icon={Users} accent="var(--accent)" extra={
              <button className="text-drobny font-semibold" style={{ color: "var(--accent)" }}
                onClick={() => setActiveTab("rodina")} data-testid="add-contact-person-hdr">{TL("+ Přidat", lang)}</button>
            }>
              <div className="text-center py-2">
                <div className="text-drobny mb-1.5" style={{ color: "var(--muted)" }}>
                  {TL("Ke klientovi nejsou evidovány žádné kontaktní osoby", lang)}
                </div>
                <button
                  onClick={() => setActiveTab("rodina")}
                  data-testid="add-contact-person"
                  className="text-drobny font-medium px-3 py-1 rounded transition-colors"
                  style={{ background: "var(--surface)", color: "var(--accent)", border: "1px solid var(--accent)" }}
                >
                  {TL("+ Přidat kontaktní osobu", lang)}
                </button>
              </div>
            </RaynetSection>

            <RaynetSection title={TL("Propojení na jiné klienty", lang)} icon={UserSquare2} accent="var(--accent)" extra={
              <button className="text-drobny font-semibold" style={{ color: "var(--accent)" }}
                onClick={() => setActiveTab("rodina")}
              >{TL("+ Přidat", lang)}</button>
            }>
              {familyMembers.length === 0 ? (
                <div className="text-center py-2">
                  <div className="text-drobny leading-relaxed mb-1.5" style={{ color: "var(--muted)" }}>
                    {lang === "sk" ? <>Nastavte rodinných alebo pridružených klientov<br />alebo vytvorte voľné prepojenie s ďalšími klientmi</> : <>Nastavte rodinné nebo přidružené klienty<br />nebo vytvořte volné propojení s dalšími klienty</>}
                  </div>
                  <button
                    onClick={() => document.getElementById("family-anchor")?.scrollIntoView({ behavior: "smooth" })}
                    className="text-drobny font-medium px-3 py-1 rounded"
                    style={{ background: "var(--surface)", color: "var(--accent)", border: "1px solid var(--accent)" }}
                  >
                    {TL("+ Propojit na jiného klienta", lang)}
                  </button>
                </div>
              ) : (
                <div id="family-anchor" className="space-y-1">
                  {familyMembers.slice(0, 6).map((m) => (
                    <button
                      key={m.id}
                      onClick={() => window.dispatchEvent(new CustomEvent("open-client-folder", { detail: { contactId: m.id } }))}
                      className="w-full flex items-center gap-1.5 px-1.5 py-1 rounded text-left transition-colors"
                      style={{ }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--accent-soft)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <div className="w-5 h-5 rounded-full flex items-center justify-center text-drobny font-bold flex-shrink-0"
                        style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
                      >{(m.name?.[0] || "?").toUpperCase()}</div>
                      <span className="text-drobny flex-1 truncate" style={{ color: "var(--ink)" }}>{m.name}{m.surname ? ` ${m.surname}` : ""}</span>
                      <span className="text-popisek uppercase tracking-wide" style={{ color: "var(--muted)" }}>{m.relation || (TL("vazba", lang))}</span>
                    </button>
                  ))}
                  <FamilyPicker contactId={c.id} initialMembers={familyMembers} />
                </div>
              )}
            </RaynetSection>

            <RaynetSection title={TL("Poznámka ke klientovi", lang)} yellow>
              <textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full rounded px-2 py-1.5 text-[12px] outline-none resize-none"
                style={{ background: "#FFFEF5", border: "1px solid #E8DCB8", color: "var(--ink)" }}
                placeholder={TL("Co je důležité o klientovi…", lang)}
                data-testid="detail-notes"
              />
            </RaynetSection>
          </div>

          {/* ───────────────────── MIDDLE col (5/12) — Portfolio donut + Smlouvy + Adresy a kontakty + Aktivity ───────────────────── */}
          <div className="md:col-span-5 space-y-1.5">

            {/* PORTFOLIO A SMLOUVY JEN U KLIENTA, NE U NÁBORÁŘE (20. 8. 2026).
             * Záložky Smlouvy/Majetek/Obchody se kandidátovi schovávají už dávno
             * (RECRUIT_HIDDEN_TABS), ale tyhle dvě karty se vykreslovaly vždycky.
             * Nad fialovým pruhem „Kandidát do týmu, ne klient" tak svítilo
             * PORTFOLIO KLIENTA 550 000 Kč, Provize YTD a Smlouvy (2) — a odkaz
             * „Smlouvy (2)" navíc přepínal na záložku, která pro náboráře
             * NEEXISTUJE, takže skončil v prázdnu.
             * Hlásil majitel: „omylem jsem udělal analýzu člověka, co naboruju,
             * a v klientské složce to vypadá divně". */}
            {!isRecruit && (
              <>
                {/* Fáze 3 — Portfolio donut (recharts) */}
                <PortfolioDonutCard
                  contracts={contracts}
                  signedBj={Number(c.signed_bj) || 0}
                  koef={koef}
                />

                {/* Fáze 3 — Smlouvy quick clean list */}
                <ContractsCleanList
                  contracts={contracts}
                  limit={4}
                  onOpenAll={() => setActiveTab("smlouvy")}
                  onOpenContract={(ct) => setEditorState({ open: true, contract: ct })}
                />
              </>
            )}

            {/* „Přidat“ v hlavičce odebráno (nefunkční placeholder bez onClick) — 2026-08.
                Adresa i kontakty se editují přímo v polích níž, tlačítko jen předstíralo akci. */}
            <RaynetSection title={t("slozkaKlienta.adresyKontakty")} icon={MapIcon}>
              <div className="mb-2 space-y-0.5">
                <div className="text-popisek uppercase tracking-wide font-bold mb-1.5" style={{ color: "var(--accent)", letterSpacing: "0.05em" }}>
                  {t("slozkaKlienta.sidloKlienta")}
                </div>
                {/* Jednotná mřížka: všechny popisky 64px → levé hrany inputů zarovnané. Ulice = našeptávač (bez duplicity). */}
                <RaynetField label={TL("Ulice", lang)} labelWidth={LW}>
                  <AddressAutocomplete
                    value={form.street}
                    onChange={(v) => setForm({ ...form, street: v })}
                    onSelect={({ street, city, psc, region, lat, lon }) => {
                      setForm((f) => ({
                        ...f, street: street || f.street, city: city || f.city, psc: psc || f.psc, region: region || f.region,
                        ...(lat != null && lon != null ? { lat, lon } : {}),  // GPS z našeptávače → mapa klientů
                      }));
                    }}
                    testid="detail-street"
                    placeholder={TL("Začněte psát ulici…", lang)}
                  />
                </RaynetField>
                <RaynetField label={TL("Město", lang)} labelWidth={LW}><InputTight value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="detail-city" /></RaynetField>
                {/* Kraj široký + PSČ kompaktní (krátké pole) — Kraj zarovnaný se zbytkem */}
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0"><RaynetField label={t("slozkaKlienta.kraj")} labelWidth={LW}><InputTight value={form.region} onChange={(v) => setForm({ ...form, region: v })} testid="detail-region" /></RaynetField></div>
                  <div className="flex-shrink-0" style={{ width: 124 }}><RaynetField label="PSČ" labelWidth={34}><InputTight value={form.psc} onChange={(v) => setForm({ ...form, psc: v })} testid="detail-psc" /></RaynetField></div>
                </div>
                {/* „vybrat“ odebráno (nefunkční placeholder bez onClick) — 2026-08.
                    Země je běžné textové pole, číselník za tím žádný není. */}
                <RaynetField label={TL("Země", lang)} labelWidth={LW}>
                  <InputTight value={form.country || adresyZeme().nazev} onChange={(v) => setForm({ ...form, country: v })} />
                </RaynetField>
                <RaynetField label={TL("Teritorium", lang)} labelWidth={LW}><InputTight value={form.territory || ""} onChange={(v) => setForm({ ...form, territory: v })} placeholder="—" /></RaynetField>
                {/* „upravit“ odebráno (nefunkční placeholder bez onClick) — 2026-08.
                    GPS se plní z našeptávače adresy výš (onSelect → lat/lon), ručně se needituje. */}
                <RaynetField label="GPS" labelWidth={LW}>
                  <span className="text-drobny flex-1 truncate" style={{ color: "var(--ink-muted)" }}>{form.city ? `${form.city}, ${form.country || adresyZeme().zkratka}` : "—"}</span>
                </RaynetField>
              </div>

              <div className="mt-2 pt-2" style={{ borderTop: "1px dashed var(--line)" }}>
                <div className="text-popisek uppercase tracking-wide font-bold mb-1" style={{ color: "var(--accent)", letterSpacing: "0.05em" }}>
                  {TL("Základní kontakty", lang)}
                </div>
                {/* Typ telefonu — historie tohohle pole ve dvou krocích:
                    1) Původně tu byl <select> s onChange={() => {}}. Poradce typ vybral,
                       nic se neuložilo a po zavření složky se vrátilo „mobil“. Backend
                       pole phone_type/phone2_type vůbec neměl, takže by ho ContactUpdateInput
                       stejně zahodil (model_dump(exclude_unset=True) nad extra="ignore").
                    2) Pole se proto dočasně skrylo — raději nic než falešný pocit uložení.
                    Teď backend pole MÁ (models.py: PhoneType = Literal[mobile|office|home]),
                    takže se select vrací se skutečným setForm. Guard test hlídá, že se
                    no-op onChange nevrátí. */}
                <RaynetField label="Tel 1" labelWidth={LW}>
                  <div className="flex items-center gap-1">
                    <InputTight value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="detail-phone" />
                    <SelectTight
                      value={form.phone_type || "mobile"}
                      onChange={(v) => setForm({ ...form, phone_type: v })}
                      testid="detail-phone-type"
                      minWidth={72}
                      options={PHONE_TYPE_OPTIONS(lang)}
                    />
                  </div>
                </RaynetField>
                <RaynetField label="Tel 2" labelWidth={LW}>
                  <div className="flex items-center gap-1">
                    <InputTight value={form.phone2 || ""} onChange={(v) => setForm({ ...form, phone2: v })} testid="detail-phone2" />
                    <SelectTight
                      value={form.phone2_type || "mobile"}
                      onChange={(v) => setForm({ ...form, phone2_type: v })}
                      testid="detail-phone2-type"
                      minWidth={72}
                      options={PHONE_TYPE_OPTIONS(lang)}
                    />
                  </div>
                </RaynetField>
                <RaynetField label="E-mail" labelWidth={LW}><InputTight value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="detail-email" /></RaynetField>
                <RaynetField label="E-mail 2" labelWidth={LW}><InputTight value={form.email2 || ""} onChange={(v) => setForm({ ...form, email2: v })} /></RaynetField>
                <RaynetField label="WWW" labelWidth={LW}><InputTight value={form.www || ""} onChange={(v) => setForm({ ...form, www: v })} placeholder="https://…" /></RaynetField>
              </div>
            </RaynetSection>

            {/* Nejbližší naplánovaná aktivita.
                Jméno poradce se ukazuje JEN když aktivita opravdu existuje — jinak by karta
                podepisovala neexistující záznam. */}
            <ActivityCard
              kind="next"
              label={TL("Nejbližší naplánovaná aktivita", lang)}
              date={c.next_activity_at}
              text={c.next_activity_label || "—"}
              owner={c.next_activity_at ? (user?.name || (TL("Poradce", lang))) : ""}
            />

            {/* Poslední zrealizovaná aktivita.
                HOTFIX pravdivosti (2026-07): date bylo `c.last_activity_at || c.updated_at` —
                bez skutečné aktivity se tu ukazoval čas poslední ZMĚNY KARTY jako datum
                „poslední zrealizované aktivity“. Fallback pryč, bez dat je datum „—“. */}
            <ActivityCard
              kind="last"
              label={TL("Poslední zrealizovaná aktivita", lang)}
              date={c.last_activity_at}
              text={c.last_activity_label || "—"}
              owner={c.last_activity_at ? (user?.name || (TL("Poradce", lang))) : ""}
            />
          </div>

          {/* ───────────────────── RIGHT col (3/12) — Finance KPI list ───────────────────── */}
          <div className="md:col-span-3 space-y-1.5">
            {/* Logo upload odebrán (nefunkční placeholder) — 2026-06-26 per user request */}

            {/* Verification mini-pills ODEBRÁNY — 2026-08.
                Tři kolečka („Ověřit“ / „Tagy“ / „Označit jako rizikový“) neměla onClick
                ani žádnou funkci za sebou: klik nic neudělal a poradce čekal akci.
                Stejný postup jako u logo uploadu a MERK tlačítka výš. Tagy navíc reálně
                řeší TagsEditor dole v řádku Štítky. */}

            {/* KPI vertical list.
                HOTFIX pravdivosti (2026-07) — zůstávají JEN metriky, které mají skutečný
                zdroj dat na klientovi. Odstraněno bylo:
                  • „Hodnota participací“ (value={0} natvrdo) — participace na cizím
                    obchodě náš datový model vůbec nezná (backend/models_deals.py: obchod
                    má jediné contact_id, žádné participanty), takže nejde ani spočítat,
                    ani poctivě zobrazit nulu. Metriku proto neukazujeme vůbec.
                  • „Rozjednáno ve skupině“ (value={0} natvrdo) — skupina firem v datech
                    neexistuje (kontakt nemá mateřskou/dceřinou vazbu), stejný důvod.
                  • „Prodáno ve skupině“ — počítalo PŘESNĚ TENTÝŽ vzorec jako „Hodnota
                    obch. vztahu“ ((signed_bj || 0) * koef), tedy dvakrát totéž číslo pod
                    dvěma jmény. Vlastní zdroj (součet za skupinu) nemá → odstraněno,
                    ponechána jen původní „Hodnota obch. vztahu“.
                  • „Počet otevřených obchodních případů“ — četlo c.open_deals_count, což
                    backend nikdy neposílá (pole není v Contact ani nikde v backend/), takže
                    to bylo trvale bez zdroje. Skutečný počet umí /api/deals?contact_id=…
                    (viz report) — dokud se nenapojí, metriku neukazujeme.
                Přejmenováno:
                  • „Hodnota otevřených obchodních případů“ → „Odhad potenciálu klienta“:
                    počítá se z c.estimate_bj, což je ruční „Odhad BJ“ poradce
                    (QuickAddContactModal), NE součet otevřených obchodů. Původní název
                    tvrdil něco jiného, než co číslo znamená. */}
            {currency?.code === "CZK" ? (
              <>
                <KpiRow label={TL("Hodnota obch. vztahu", lang)} value={penizeZBj(c.signed_bj)} accent />
                <KpiRow label={t("slozkaKlienta.odhadPotencialu")} value={penizeZBj(c.estimate_bj)} accent />
              </>
            ) : (
              <div className="text-drobny px-1 py-px" data-testid="kpi-hodnota-chybi" style={{ color: "var(--ink-muted)" }}>
                {TL("Hodnota obch. vztahu", lang)}: {hlaskaBezKoeficientu(lang)}
              </div>
            )}
            <KpiRow label={TL("Následující aktivita za", lang)}
              value={
                nextActivityDays === null ? "—"
                : nextActivityDays < 0 ? `${Math.abs(nextActivityDays)} d`
                : nextActivityDays === 0 ? "dnes"
                : `+${nextActivityDays} d`
              }
              small
            />
            <KpiRow label={TL("Poslední aktivita před", lang)} value={lastActivityDays === null ? "—" : lastActivityDays === 0 ? "dnes" : `${lastActivityDays} d`} small />

            <ReferralLinkButton contact={c} />
          </div>
          </div>

          {/* Bottom tags row — Štítky */}
          <div className="mt-3 pt-2 flex items-center gap-1.5 flex-wrap" style={{ borderTop: "1px solid var(--line)" }}>
            <span className="text-popisek uppercase font-bold tracking-wider mr-1" style={{ color: "var(--muted)", letterSpacing: "0.05em" }}>{t("slozkaKlienta.stitky")}</span>
            {/* Plus odebrán (nefunkční placeholder bez onClick) — 2026-08. Štítek se
                přidává přímo v TagsEditoru vedle (vstup + Enter + jeho vlastní plus). */}
            <TagsEditor contactId={c.id} initialTags={tags} onChange={(next) => setTags(next)} />
          </div>
        </div>
        )}

        {activeTab === "dalsi" && (
          <DalsiUdajeTab contact={c} onPatch={onPatch} />
        )}

        {activeTab === "rodina" && (
          <div className="px-5 md:px-8 py-6 grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="tab-rodina">
            <div className="md:col-span-7">
              <HouseholdGraph
                centerName={`${c.name || ""} ${c.surname || ""}`.trim() || "Klient"}
                centerPhoto={photoUrl}
                members={familyMembers}
                onAdd={() => document.getElementById("rodina-picker")?.scrollIntoView({ behavior: "smooth" })}
                onPickMember={(m) => window.dispatchEvent(new CustomEvent("open-client-folder", { detail: { contactId: m.id } }))}
                large
              />
            </div>
            <div className="md:col-span-5" id="rodina-picker">
              <RaynetSection title={TL("Propojit s další osobou", lang)}>
                <FamilyPicker contactId={c.id} initialMembers={familyMembers} />
              </RaynetSection>
            </div>
          </div>
        )}

        {activeTab === "smlouvy" && (
          <div className="px-5 md:px-8 py-6" data-testid="tab-smlouvy">
            {contractsError ? (
              <div className="text-center py-10" data-testid="contracts-error">
                <div className="text-[13px] font-semibold" style={{ color: "var(--negative-text)" }}>
                  {TL("Smlouvy se nepodařilo načíst", lang)}
                </div>
                <div className="text-drobny mt-1" style={{ color: "var(--muted)" }}>
                  {TL("Neznamená to, že klient smlouvy nemá — data se nenačetla.", lang)}
                </div>
                <button onClick={refreshContracts} data-testid="contracts-retry"
                  className="mt-3 px-4 py-2 rounded-full text-[12px] font-semibold"
                  style={{ background: "var(--ink, #26221A)", color: "var(--bg, #F7F4EC)" }}>
                  {TL("Zkusit znovu", lang)}
                </button>
              </div>
            ) : (
            <ContractsTab
              contracts={contracts}
              contactId={c.id}
              onAdd={() => setEditorState({ open: true, contract: null })}
              onEdit={(ct) => setEditorState({ open: true, contract: ct })}
            />
            )}
          </div>
        )}

        {activeTab === "majetek" && (
          <div className="px-5 md:px-8 py-6" data-testid="tab-majetek">
            {/* Rozvaha nahoře: klient měl jmění rozházené ve smlouvách a v majetku
                a nikde to nebylo vidět dohromady (zadání majitele 10. 8. 2026).
                Čísla počítá lib/crm/rozvaha.js stejným vzorcem jako lib/bilance.js,
                aby na dvou obrazovkách nevyšlo jiné čisté jmění. */}
            <RozvahaGraf r={rozvahaZobrazit} fmtMoney={fmtMoneyDat} lang={lang} />
            <MajetekTab
              majetek={assets}
              /* Stejný formátovač jako rozvaha nad ním: majetek z analýzy nese
                 MĚNU DAT (A3.3). Bez analýzy je to měna účtu jako dřív. */
              fmtMoney={fmtMoneyDat}
              lang={lang}
              chyba={assetsError}
              onRetry={refreshAssets}
            />
          </div>
        )}

        {activeTab === "hypoteka" && (
          <div className="px-5 md:px-8 py-6" data-testid="tab-hypoteka">
            <HypotekaDotaznikKarta
              contact={c}
              /* Poradce pro brandovanou hlavičku tisku a pro podpis zprávy.
                 Vlastní odesílání tu není a nebude — karta jen připraví text. */
              advisor={user}
              lang={lang}
            />
          </div>
        )}

        {activeTab === "formulare" && (
          <div className="px-5 md:px-8 py-6" data-testid="tab-formulare">
            <KlientskeFormularKarta contact={c} />
          </div>
        )}

        {activeTab === "obchody" && (
          <div className="px-5 md:px-8 py-6" data-testid="tab-obchody">
            <ClientDealsTab
              contactId={c.id}
              contactName={`${c.name || ""} ${c.surname || ""}`.trim() || "klient"}
            />
          </div>
        )}

        {activeTab === "historie" && (
          <div className="px-5 md:px-8 py-6" data-testid="tab-historie">
            <HistoryTimeline
              contactId={c.id}
              advisorName={user?.name || (TL("Poradce", lang))}
              advisorAvatar={user?.photo_url}
            />
          </div>
        )}

        {activeTab === "diskuze" && (
          <div className="px-5 md:px-8 py-6" data-testid="tab-diskuze">
            <ClientDiscussionTab
              contactId={c.id}
              contactName={`${c.name || ""} ${c.surname || ""}`.trim() || "klient"}
            />
          </div>
        )}

        {activeTab === "prilezitosti" && (
          <div className="px-5 md:px-8 py-6 space-y-5" data-testid="tab-prilezitosti">
            <AIWatchdogOpportunities contactId={c.id} />
            <div className="pt-2" style={{ borderTop: "1px solid var(--line)" }}>
              <div className="text-popisek uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: "var(--accent)" }}>
                <Radar size={10} className="inline mr-1" /> {TL("Coverage radar (chybějící produkty)", lang)}
              </div>
              <LeakageRadar contactId={c.id} />
            </div>
          </div>
        )}
        </div>{/* /scroll container */}

        {/* Footer save (Elite-Light glass) — always visible at bottom */}
        {/* Mimo celou obrazovku leží panel pod spodní navigací (lg:hidden, 64 px) —
            patička se odsouvá nad ni, ať „Do koše" nekoliduje (CRM-05). */}
        <div className={`glass-bar px-5 md:px-8 py-3 flex items-center justify-between flex-shrink-0 ${fullScreen ? "" : "mb-16 lg:mb-0"}`} style={{ borderTop: "1px solid var(--line)", borderBottom: "none" }} data-testid="detail-footer">
          <button
            onClick={onDelete}
            className="text-xs flex items-center gap-1 transition-colors hover:opacity-80"
            style={{ color: "var(--accent, #8C3B2E)" }}
            data-testid="detail-delete"
            title={TL("Přesunout klienta do koše — kdykoli jde obnovit v Klienti → Koš", lang)}
          >
            <Trash2 size={13} /> {TL("Do koše", lang)}
          </button>
          <div className="flex items-center gap-3">
            {/* Stavový indikátor: beze změn / neuložené / ukládám / uloženo / chyba */}
            <span
              className="text-drobny font-medium select-none"
              data-testid="save-status"
              data-save-state={saving ? "saving" : saveError ? "error" : dirty ? "dirty" : savedOk ? "saved" : "idle"}
              style={{ color: saveError ? "#C53030" : dirty ? "var(--accent, #8C3B2E)" : "var(--muted, #737373)" }}
            >
              {saving ? (TL("Ukládám…", lang))
                : saveError ? (TL("Uložení selhalo", lang))
                : dirty ? (TL("Neuložené změny", lang))
                : savedOk ? (TL("Uloženo ✓", lang))
                : (TL("Beze změn", lang))}
            </span>
            <button
              onClick={save}
              disabled={saving || (!dirty && !saveError)}
              data-testid="detail-save"
              className="px-5 py-2 rounded-full font-semibold text-sm transition-all hover:translate-y-[-1px] disabled:opacity-50"
              style={{
                background: saveError ? "#C53030" : "var(--ink, #26221A)",
                color: "var(--bg, #F7F4EC)",
                boxShadow: "var(--shadow-card, 0 2px 10px rgba(38,34,26,0.05))",
              }}
            >
              {saving ? (TL("Ukládám…", lang))
                : saveError ? (TL("Zkusit uložit znovu", lang))
                : (TL("Uložit změny", lang))}
            </button>
          </div>
        </div>

        <ContractEditor
          open={editorState.open}
          contract={editorState.contract}
          contactId={c.id}
          onClose={() => setEditorState({ open: false, contract: null })}
          onSaved={() => refreshContracts()}
          onDeleted={() => refreshContracts()}
        />

        <VoiceFillModal
          open={voiceFillOpen}
          onClose={() => setVoiceFillOpen(false)}
          onApply={async (patch) => {
            // Merge into form for immediate UI update
            setForm((f) => ({ ...f, ...patch }));
            // Persist to backend (okamžité autosave)
            try {
              await onPatch(patch);
              setBaseline((b) => ({ ...b, ...patch })); // uloženo → baseline zohledni (nezůstane „dirty")
            } catch { /* onPatch shows toast */ }
          }}
        />

        <GmailComposerModal
          open={gmailComposerOpen}
          onClose={() => setGmailComposerOpen(false)}
          contact={c}
          advisorName={user?.name || ""}
          onSent={() => { /* future: refresh history */ }}
        />

        {aiEmailOpen && (
          <AIEmailDraftModal
            onClose={() => setAiEmailOpen(false)}
            onSent={() => setAiEmailOpen(false)}
            contactId={c.id}
            contactName={`${c.name || ""} ${c.surname || ""}`.trim()}
          />
        )}

        <WhatsAppSender
          open={waOpen}
          onClose={() => setWaOpen(false)}
          contact={c}
          advisorName={user?.name || ""}
          advisorSlug={user?.profile?.vizitka_slug || ""}
        />

        {/* Dirty-guard — bezpečnostní dialog při zavírání s neuloženými změnami.
            Žádná tichá ztráta rozepsaných dat: Zůstat a uložit / Zahodit / Zrušit. */}
        {confirmClose && (
          <div
            className="fixed inset-0 z-[95] flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.45)" }}
            data-testid="dirty-close-dialog"
            onClick={cancelClose}
          >
            <div
              className="elite-card w-full max-w-sm p-5"
              style={{ background: "var(--surface, #fff)" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={18} style={{ color: "var(--accent, #8C3B2E)" }} />
                <h3 className="text-base font-semibold" style={{ color: "var(--ink, #26221A)" }}>
                  {TL("Neuložené změny", lang)}
                </h3>
              </div>
              <p className="text-sm mb-4" style={{ color: "var(--muted, #737373)" }}>
                {lang === "sk"
                  ? "Máš rozpísané zmeny, ktoré ešte nie sú uložené. Čo s nimi?"
                  : "Máš rozepsané změny, které ještě nejsou uložené. Co s nimi?"}
              </p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => runProceed("save")}
                  disabled={saving}
                  data-testid="dirty-save-close"
                  className="px-4 py-2 rounded-full font-semibold text-sm disabled:opacity-50"
                  style={{ background: "var(--ink, #26221A)", color: "var(--bg, #F7F4EC)" }}
                >
                  {saving ? (TL("Ukládám…", lang)) : (TL("Uložit a pokračovat", lang))}
                </button>
                <button
                  onClick={() => runProceed("discard")}
                  data-testid="dirty-discard-close"
                  className="px-4 py-2 rounded-full font-medium text-sm"
                  style={{ border: "1px solid var(--line, #E3E0D8)", color: "var(--negative-text)" }}
                >
                  {TL("Zahodit změny", lang)}
                </button>
                <button
                  onClick={cancelClose}
                  data-testid="dirty-cancel-close"
                  className="px-4 py-2 rounded-full font-medium text-sm"
                  style={{ color: "var(--muted, #737373)" }}
                >
                  {TL("Zůstat ve složce", lang)}
                </button>
              </div>
            </div>
          </div>
        )}
      </motion.aside>
    </>
  );
}

function initForm(c) {
  return {
    name: c.name || "", surname: c.surname || "",
    phone: c.phone || "", phone2: c.phone2 || "",
    // Typ telefonu musí být i TADY, ne jen v <select>. initForm je whitelist —
    // co v něm chybí, to se do baseline nedostane, takže by se uložená hodnota
    // po znovuotevření složky nezobrazila a select by zase ukazoval „mobil".
    // Přesně to poradci vadilo na původní verzi.
    phone_type: c.phone_type || "mobile",
    phone2_type: c.phone2_type || "mobile",
    email: c.email || "", email2: c.email2 || "",
    www: c.www || "",
    city: c.city || "", street: c.street || "", psc: c.psc || "", region: c.region || "",
    // Výchozí stát = země PORADCE (SK tenant → Slovensko), ne natvrdo ČR (CRM-SK-03).
    country: c.country || adresyZeme().nazev,
    territory: c.territory || "",
    company: c.company || "", ico: c.ico || "",
    dob: c.dob || "", marital_status: c.marital_status || "",
    estimate_bj: c.estimate_bj ?? "", signed_bj: c.signed_bj ?? "",
    notes: c.notes || "",
    calling_date: c.calling_date || "",
    a1_date: c.a1_date || "", a2_date: c.a2_date || "", p1_date: c.p1_date || "",
    p2_date: c.p2_date || "", sign_date: c.sign_date || "",
    products: c.products || {},
    category: c.category || "B",
    source: c.source || "",
    status: c.status || "active",
    relation: c.relation || "client",
    rating: c.rating ?? 0,
    industry: c.industry || "",
    is_physical_person: !!c.is_physical_person,
  };
}

function Block({ title, icon: Icon, extra, children }) {
  return (
    <div className="elite-card">
      <div className="flex items-center justify-between px-3.5 py-2" style={{ borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
        <div className="flex items-center gap-1.5 text-popisek uppercase tracking-[0.18em] font-semibold" style={{ color: "#737373" }}>
          {Icon && <Icon size={12} />}
          {title}
        </div>
        {extra}
      </div>
      <div className="p-3.5 space-y-2.5">{children}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * RAYNET 1:1 COMPONENTS — solid header bar (cognac) + dense field layout
 * ═══════════════════════════════════════════════════════════════════════════ */

/* RaynetSection — block with SUBTLE muted header (small icon + uppercase muted title).
 * v4.21.2 (Fáze 3) — Soft header per Sage & Clay / Indigo design system.
 *  - Card bg: var(--surface), border: var(--line), radius: 14px, shadow: var(--shadow-card)
 *  - Header: small accent-tinted icon + uppercase var(--muted) title + thin hairline divider
 *  - Yellow variant (Poznámka ke klientovi) keeps Raynet-style warm warning tone.
 */
function RaynetSection({ title, children, extra, yellow = false, icon: Icon, accent = "var(--accent)" }) {
  if (yellow) {
    // Calm yellow notice card — no orange, just warm cream surface.
    return (
      <section
        className="overflow-hidden rounded-2xl elite-shadow"
        style={{
          /* Tokeny (CRM-09): dřív krém #FFFDF5 = světlý ostrov v Midnight. */
          background: "color-mix(in srgb, var(--warn) 6%, var(--surface))",
          border: "1px solid color-mix(in srgb, var(--warn) 30%, transparent)",
        }}
      >
        <header
          className="flex items-center gap-2 px-3 pt-2.5 pb-1.5"
          style={{ borderBottom: "1px solid color-mix(in srgb, var(--warn) 22%, transparent)" }}
        >
          <span
            className="inline-flex items-center justify-center rounded-md"
            style={{ width: 18, height: 18, background: "color-mix(in srgb, var(--warn) 18%, var(--surface))", color: "var(--warn-text)" }}
          >
            <Pencil size={11} strokeWidth={2.4} />
          </span>
          <div
            className="text-popisek font-bold uppercase tracking-[0.08em] flex-1 truncate"
            style={{ color: "var(--warn-text)" }}
          >
            {title}
          </div>
          {extra}
        </header>
        <div className="px-3 py-2.5">{children}</div>
      </section>
    );
  }
  return (
    <section
      className="overflow-hidden rounded-2xl elite-shadow ring-hairline"
      style={{ background: "var(--surface)" }}
    >
      {/* Barevná lišta sekce (á la Raynet) — tónované pozadí + svislý proužek + barevný nadpis. */}
      <header
        className="relative flex items-center gap-2 pl-4 pr-3 py-1.5"
        style={{
          background: `color-mix(in srgb, ${accent} 9%, var(--surface))`,
          borderBottom: `1px solid color-mix(in srgb, ${accent} 24%, var(--line))`,
        }}
      >
        <span className="absolute left-0 top-0 bottom-0" style={{ width: 3, background: accent }} aria-hidden />
        {Icon && (
          <span
            className="inline-flex items-center justify-center rounded-md"
            style={{ width: 18, height: 18, background: `color-mix(in srgb, ${accent} 16%, transparent)`, color: accent }}
          >
            <Icon size={11} strokeWidth={2.4} />
          </span>
        )}
        <div
          className="text-popisek font-bold uppercase tracking-[0.08em] flex-1 truncate"
          style={{ color: accent }}
        >
          {title}
        </div>
        {extra}
      </header>
      <div className="px-3 py-1.5">{children}</div>
    </section>
  );
}

/* RaynetField — single row: label left (fixed width), input right (flex-1).
 * Density: 24-26px row height, 10px label. Matches Raynet exactly. */
function RaynetField({ label, children, labelWidth = 64 }) {
  return (
    <div className="flex items-center gap-1.5 py-0.5">
      <div
        className="text-drobny font-medium flex-shrink-0"
        style={{ color: "var(--ink-muted)", width: labelWidth }}
      >
        {label}:
      </div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

/* RatingStars — 3-star rating with cognac fill */
function RatingStars({ value = 0, onChange }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[12px] font-bold" style={{ color: "var(--ink)" }}>
        {value >= 4 ? "A" : value >= 3 ? "B" : value >= 2 ? "C" : "—"}
      </span>
      <div className="flex items-center gap-0.5">
        {[1, 2, 3].map((i) => (
          <button
            key={i}
            onClick={() => onChange(i + 2)} // 1->3 (A), 2->4 (B+), 3->5 (A+)
            className="transition-transform hover:scale-110"
          >
            <Star
              size={12}
              fill={value >= i + 2 ? "#D69412" : "transparent"}
              stroke={value >= i + 2 ? "#D69412" : "#A29B8C"}
              strokeWidth={1.5}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

/* ActivityCard — Nejbližší/Poslední aktivita card (Raynet 1:1) */
function ActivityCard({ kind, label, date, text, owner }) {
  const dateFmt = date ? new Date(date).toLocaleDateString(localeUI()) : "—";
  const Icon = kind === "next" ? Calendar : FileCheck;
  return (
    <section
      className="overflow-hidden rounded-2xl elite-shadow ring-hairline"
      style={{ background: "var(--surface)" }}
      data-testid={`activity-${kind}`}
    >
      <header
        className="flex items-center gap-2 px-3 pt-2.5 pb-1.5"
        style={{ borderBottom: "1px solid var(--line)" }}
      >
        <span
          className="inline-flex items-center justify-center rounded-md"
          style={{ width: 18, height: 18, background: "var(--accent-soft)", color: "var(--accent)" }}
        >
          <Icon size={11} strokeWidth={2.4} />
        </span>
        <div
          className="text-popisek font-bold uppercase tracking-[0.08em] flex-1 truncate"
          style={{ color: "var(--muted)" }}
        >
          {label}
        </div>
      </header>
      <div className="px-3 py-2">
        <div className="text-drobny flex items-baseline gap-1.5">
          <span className="mono font-semibold tabular-nums" style={{ color: "var(--ink)" }}>{dateFmt}</span>
          <span className="truncate" style={{ color: "var(--ink)" }}>{text}</span>
        </div>
        <div className="text-drobny mt-0.5" style={{ color: "var(--muted)" }}>
          {owner}
        </div>
      </div>
    </section>
  );
}

/* KpiRow — jednořádkový Finance KPI (label vlevo muted, hodnota vpravo).
 * accent = peníze → zelená var(--money-dark) dle Inkoustových gradientů.
 * Prázdné/nulové hodnoty (0 Kč, 0, "") → jemná pomlčka místo 2řádkového bloku. */
function KpiRow({ label, value, accent = false, small = false }) {
  const isEmpty = value === 0 || value === "0" || value === "0 Kč" || value === "" || value == null;
  return (
    <div className="flex items-baseline justify-between gap-2 px-1 py-px">
      <span className="text-drobny leading-tight truncate min-w-0" style={{ color: "var(--ink-muted)" }}>
        {label}
      </span>
      <span
        className={`mono tabular-nums font-bold ${small ? "text-drobny" : "text-[13px]"} leading-tight flex-shrink-0`}
        style={{ color: isEmpty ? "var(--ink-faint)" : accent ? "var(--money-text)" : "var(--ink)" }}
      >
        {isEmpty ? "—" : value}
      </span>
    </div>
  );
}

/* v4.11 — Compact inline label:value row (Raynet density).
 * Label vlevo (~80px), input vpravo flex-1. ~28px výška. */
function RowField({ label, children, labelWidth = 88 }) {
  return (
    <div className="flex items-center gap-2 py-0.5">
      <div
        className="text-popisek uppercase tracking-wide font-medium flex-shrink-0"
        style={{ color: "var(--ink-muted)", width: labelWidth, letterSpacing: "0.04em" }}
      >
        {label}
      </div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

/* Tightly-packed block (Raynet style) — smaller padding, no big icon, optional badge. */
function TightBlock({ title, children, extra }) {
  return (
    <section
      className="rounded-lg"
      style={{ background: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--elite-shadow)" }}
    >
      <header
        className="flex items-center gap-2 px-3 py-1.5"
        style={{ background: "linear-gradient(180deg, #FCF7F0, #FAF4EC)", borderBottom: "1px solid var(--border)", borderTopLeftRadius: 8, borderTopRightRadius: 8 }}
      >
        <div className="text-popisek font-bold uppercase tracking-wide flex-1" style={{ color: "var(--accent)", letterSpacing: "0.06em" }}>
          {title}
        </div>
        {extra}
      </header>
      <div className="px-3 py-2">{children}</div>
    </section>
  );
}

/* Inline compact input — height 26px, no internal padding around field */
/* readOnly = pole jen zobrazuje hodnotu ze systému a NEJDE do něj psát.
 * Používá se tam, kde dřív byl editovatelný input s onChange={() => {}} — uživatel
 * do něj napsal text a systém ho tiše zahodil. Raději nejde editovat, než aby to
 * lhalo o tom, že se změna uloží. */
/* Volby typu telefonu. Hodnoty MUSÍ sedět na backendový Literal
   PhoneType = ["mobile", "office", "home"] (backend/models.py) — cokoli jiného
   vrátí 422. Proto jedno místo, ne dvě kopie u Tel 1 a Tel 2. */
const PHONE_TYPE_OPTIONS = (lang) => [
  { v: "mobile", label: textJazyka(lang, "slozkaKlienta.mobil") },
  { v: "office", label: "kanc." },
  { v: "home", label: TL("domů", lang) },
];

function InputTight({ value, onChange, type = "text", testid, placeholder, readOnly = false, title }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      readOnly={readOnly}
      title={title}
      onChange={(e) => { if (!readOnly) onChange?.(e.target.value); }}
      data-testid={testid}
      placeholder={placeholder}
      // výška 28 + nativní centrování (lineHeight normal) — text se neořezává
      className="w-full rounded px-2 text-maly outline-none transition-colors"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--line)",
        color: "var(--ink)",
        height: 28,
        lineHeight: "normal",
      }}
      onFocus={(e) => (e.target.style.borderColor = "var(--accent)")}
      onBlur={(e) => (e.target.style.borderColor = "var(--line)")}
    />
  );
}

function SelectTight({ value, onChange, options, testid, minWidth }) {
  // Ph4B fix: native <select> s pevnou height:28 + lineHeight:normal na macOS
  // vertikálně OŘEZÁVAL text (Stav/Vztah/Tel byly nečitelné — viz reálný screenshot).
  // appearance:none vypne nativní chrome, takže text se centruje jako v <input> a
  // neořízne se; vlastní šipka vpravo (pr-6). lineHeight 26px = obsah 28px boxu.
  return (
    <div className="relative flex items-center" style={{ width: minWidth || "100%", minWidth: minWidth || 0 }}>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full rounded pl-2 pr-6 text-maly outline-none cursor-pointer appearance-none truncate"
        style={{ background: "var(--surface)", border: "1px solid var(--line)", color: "var(--ink)", height: 28, lineHeight: "26px", paddingTop: 0, paddingBottom: 0, WebkitAppearance: "none", MozAppearance: "none" }}
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
      <svg className="pointer-events-none absolute right-1.5" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden style={{ color: "var(--muted)" }}>
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
  );
}

function InputDark({ value, onChange, type = "text", testid, placeholder }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      placeholder={placeholder}
      className="w-full rounded-md px-2.5 py-1.5 text-sm outline-none transition-colors"
      style={{
        background: "var(--surface)",
        border: "1px solid rgba(0,0,0,0.12)",
        color: "var(--ink)",
      }}
      onFocus={(e) => (e.target.style.borderColor = "#C25934")}
      onBlur={(e) => (e.target.style.borderColor = "rgba(0,0,0,0.12)")}
    />
  );
}

/* v4.11 — Compact FinTile (Raynet density). 2x size of compact info row. */
function FinTileMini({ label, value, sub, accent }) {
  return (
    <div
      className="rounded-lg px-2.5 py-2"
      style={{ background: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--elite-shadow)" }}
      data-testid={`fin-mini-${label.toLowerCase().replace(/[\s.]/g, "-")}`}
    >
      <div className="text-popisek uppercase tracking-wider font-bold" style={{ color: "var(--ink-muted)", letterSpacing: "0.05em" }}>
        {label}
      </div>
      <div className="text-[14px] font-bold mono tabular-nums mt-0.5" style={{ color: accent || "var(--ink)" }}>
        {value}
      </div>
      {sub && (
        <div className="text-drobny mt-0.5 truncate" style={{ color: "var(--ink-faint)" }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function FinTile({ label, value, sub, accent, icon: Icon, testid }) {
  return (
    <div
      data-testid={testid}
      className="elite-card relative p-3.5 overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: accent }} />
      <div className="flex items-center justify-between gap-2">
        <div className="text-popisek uppercase tracking-[0.18em] font-semibold" style={{ color: "#737373" }}>{label}</div>
        {Icon && <Icon size={13} style={{ color: accent }} />}
      </div>
      <div className="font-display text-xl mt-1 tabular-nums leading-none" style={{ color: "var(--ink)", fontWeight: 600 }}>
        {value}
      </div>
      <div className="text-drobny mt-1" style={{ color: "#737373" }}>{sub}</div>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────────────── *
 * KpiBlobCard — hero KPI card with abstract organic blob decoration
 * (Adviser Hub Elite-2026 inspired). Each card has:
 *  • colored icon tile top-left
 *  • soft organic blob top-right corner in matching color
 *  • uppercase eyebrow label + big tabular value
 * ────────────────────────────────────────────────────────────────────────── */
function KpiBlobCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <div
      className="elite-card relative p-3.5 overflow-hidden"
      data-testid={testid}
      style={{ minHeight: 88 }}
    >
      {/* Abstract organic blob top-right — the signature decoration */}
      <svg
        className="absolute -top-6 -right-6 pointer-events-none"
        width="96" height="96" viewBox="0 0 100 100"
        style={{ opacity: 0.18 }}
        aria-hidden
      >
        <defs>
          <radialGradient id={`blob-grad-${testid}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={accent} stopOpacity="1" />
            <stop offset="60%" stopColor={accent} stopOpacity="0.5" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </radialGradient>
        </defs>
        <path
          d="M 30,15 Q 70,5 85,30 Q 95,55 80,80 Q 55,95 30,85 Q 10,65 15,40 Z"
          fill={`url(#blob-grad-${testid})`}
        />
      </svg>
      <div className="relative flex items-start gap-2.5">
        {Icon && (
          <div
            className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
            style={{
              background: `${accent}14`,
              outline: `1px solid ${accent}33`,
              outlineOffset: "-1px",
            }}
          >
            <Icon size={16} style={{ color: accent }} strokeWidth={2.2} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-popisek uppercase tracking-[0.18em] font-semibold" style={{ color: "#737373" }}>
            {label}
          </div>
          <div className="font-display text-[18px] mt-0.5 tabular-nums leading-tight truncate" style={{ color: "var(--ink)", fontWeight: 700 }}>
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Odkaz na doporučení — od 14. 10. 2026 SDÍLENÁ komponenta
 * (components/doporuceni/OdkazNaDoporuceni.js). Tatáž ho nabízí na konci
 * prezentace plánu a v gentlemanské dohodě analýzy; dřív tu byla vlastní
 * kopie. Chování zůstává: klik → celá zpráva do schránky → upravitelný text,
 * WhatsApp a Kopírovat (testid generate-referral-link / referral-*). Nově
 * QR, SMS a e-mail s telefonem a e-mailem klienta. Oslovení skloňuje
 * osloveniProZpravu uvnitř komponenty (dřív split(" ")[0] vracel
 * vietnamskému klientovi příjmení). */
function ReferralLinkButton({ contact }) {
  const { lang } = useLanguage();
  /* Odměna tipaři a podpis z Nastavení (15. 10. 2026) — tatáž věta jako web /d/. */
  const odmena = useOdmenaTipare();
  const poradce = usePodpisDoporuceni();
  return (
    <OdkazNaDoporuceni
      contactId={contact?.id}
      jmeno={contact?.name || ""}
      telefon={contact?.phone || ""}
      email={contact?.email || ""}
      lang={lang}
      odmena={odmena}
      poradce={poradce}
    />
  );
}

// (EliteKpiCard odebrán 4. 7. 2026 — velký KPI proužek pod hero zrušen, viz komentář výše.)

// ─── Tabbed shell components ────────────────────────────────────────────────

/* Náborový kontakt = kandidát do týmu, ne klient. Záložky, které u něj nedávají
 * smysl (nemá smlouvy, obchody ani příležitosti), se schovají a místo nich je
 * první záložka Nábor. Data jsou pořád na tomtéž kontaktu — nic se nekopíruje. */
const RECRUIT_TAB = { key: "nabor", label: "Nábor", icon: UserPlus };
// Kandidát na poradce nemá klientské produkty — majetek k němu patří stejně
// málo jako smlouvy a obchody.
const RECRUIT_HIDDEN_TABS = ["smlouvy", "majetek", "hypoteka", "formulare", "obchody", "prilezitosti"];

const TABS = [
  { key: "karta",        label: "Karta",         icon: IdCard },
  { key: "dalsi",        label: "Další údaje",   icon: FileText },
  { key: "rodina",       label: "Rodina",        icon: Users },
  { key: "smlouvy",      label: "Smlouvy",       icon: ClipboardList },
  // Majetek stojí VEDLE smluv, ne v nich: byt není smlouva a nesmí u něj
  // složka nabízet „Výpověď do pojišťovny".
  { key: "majetek",      label: "Majetek",       icon: Landmark },
  // Hypotéka má vlastní záložku, protože dotazník pro banku sbírá rodné číslo
  // a čísla dokladů. Nesmí se míchat mezi běžné údaje na Kartě, kde by po
  // otevření složky svítil na obrazovce i uprostřed schůzky o něčem jiném.
  { key: "hypoteka",     label: "Hypotéka",      icon: Home },
  // Formuláře, které poradce posílá klientovi odkazem (zdravotní dotazník,
  // podklad k pojistné smlouvě). Vlastní záložka ze stejného důvodu jako
  // hypotéka: zdravotní dotazník nesmí svítit na Kartě uprostřed schůzky
  // o něčem jiném. Hypoteční dotazník zůstává vedle — má vlastní tisk
  // pro banku a můžou být venku živé odkazy /hd/:token.
  { key: "formulare",    label: "Formuláře",     icon: ClipboardList },
  { key: "obchody",      label: "Obchody",       icon: Briefcase },
  { key: "historie",     label: "Historie",      icon: History },
  { key: "diskuze",      label: "Diskuze",       icon: Sparkles },
  { key: "prilezitosti", label: "Příležitosti",  icon: AlertTriangle },
];

const TAB_LABELS_SK = {
  nabor: "Nábor",
  karta: "Karta",
  dalsi: "Ďalšie údaje",
  rodina: "Rodina",
  smlouvy: "Zmluvy",
  majetek: "Majetok",
  hypoteka: "Hypotéka",
  formulare: "Formuláre",
  obchody: "Obchody",
  historie: "História",
  diskuze: "Diskusia",
  prilezitosti: "Príležitosti",
};

function ClientTabs({ activeTab, setActiveTab, tabs = TABS }) {
  const { lang, t } = useLanguage();
  // Šipkami doleva/doprava přepínáš záložky (a11y).
  const onKey = (e) => {
    const i = tabs.findIndex((t) => t.key === activeTab);
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const next = e.key === "ArrowRight" ? (i + 1) % tabs.length : (i - 1 + tabs.length) % tabs.length;
      setActiveTab(tabs[next].key);
    }
  };
  return (
    // overflow-x-auto → na úzké šířce jdou všechny záložky doscrollovat (nic se neoře).
    // Elite kit — pill switcher: aktivní pilulka (var(--ink)) klouže přes motion.span layoutId.
    <div className="glass-bar px-4 md:px-6 pt-2.5 pb-2.5 z-[5] flex-shrink-0 overflow-x-auto" style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "none", borderBottom: "1px solid var(--line)" }}>
      <div
        className="inline-flex items-center gap-1"
        data-testid="client-tabs"
        role="tablist" aria-label={t("slozkaKlienta.zalozkyKlienta")} onKeyDown={onKey}
      >
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              data-testid={`tab-trigger-${t.key}`}
              role="tab" aria-selected={active} tabIndex={active ? 0 : -1}
              ref={(el) => { if (el && active) el.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" }); }}
              className="relative flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-semibold flex-shrink-0 whitespace-nowrap transition-colors"
              style={{
                color: active ? "var(--bg)" : "var(--muted)", /* CRM-01: na pilulce var(--ink) — bílá v Midnight mizela (1,05 : 1) */
              }}
            >
              {active && (
                <motion.span
                  layoutId="client-folder-tab-pill"
                  className="absolute inset-0 rounded-full"
                  style={{ background: "var(--ink)", zIndex: 0 }}
                  /* 25. 10. 2026: tween 250 ms --ease-smooth-out místo pružiny (360/30
                     překmitávala) — záložky se přepínají desítkykrát denně, pohyb má
                     být krátký a bez poskočení (CLAUDE.md „Grafy a pohyb“). */
                  transition={{ duration: UI, ease: EASE_OUT }}
                />
              )}
              <Icon size={12} className="relative" style={{ zIndex: 1 }} />
              <span className="tracking-tight relative" style={{ zIndex: 1 }}>{lang === "sk" ? (TAB_LABELS_SK[t.key] || t.label) : t.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
